/**
 * The Sort trigger, shared by the view header, `/feed` and `/saved` (§12.3).
 *
 * One state, not two: the applied sort is **always** spelled out — direction arrow plus the field
 * name — including at each screen's own default. The two-state trigger it replaces went icon-only at
 * the default, which meant the one place where the ordering is never shown anywhere else (a feed,
 * where there are no column headers) was also the place that said nothing about it.
 *
 * The class names are the old ones on purpose: `view-header__sort-label` still carries the 76px
 * ellipsis cap, and the Posts flat bar still drops the label below 1420px, where the grid header
 * says which column is sorted.
 */

import { Menu, SortArrowIcon } from '../../components';
import { SortPanel } from './SortPanel';
import { sortKeyLabel, type ViewSort } from './viewState';

export type SortMenuProps = {
  sort: ViewSort;
  onChange: (next: ViewSort) => void;
  /** Opens the Author sort key — true for a group view and for both multi-author screens. */
  isGroup: boolean;
};

export function SortMenu({ sort, onChange, isGroup }: SortMenuProps) {
  const label = sortKeyLabel(sort.key);

  return (
    <Menu
      label={
        <>
          {/* The arrow *replaces* the neutral sort glyph — one icon either way, which is what keeps
              the widened button cheap. */}
          <SortArrowIcon dir={sort.dir} size={12} />
          <span className="table-action__label view-header__sort-label">{label}</span>
        </>
      }
      // `SortArrowIcon` is aria-hidden, so the direction only reaches AT here — and `Menu` mirrors
      // this into `title`, the recovery path when the label ellipsises or is dropped.
      ariaLabel={`Sort: ${label}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}`}
      align="right"
      panelClassName="menu__panel--pad"
    >
      {() => <SortPanel sort={sort} onChange={onChange} isGroup={isGroup} />}
    </Menu>
  );
}

export default SortMenu;
